import React from "react";
import { Box, CheckboxCard, IconButton, Input, Select, Text, Textarea, Tooltip } from "@chakra-ui/react";
import { FiTrash2 } from "react-icons/fi";

const inputBase = {
  h: "11",
  px: "4",
  rounded: "pill",
  bg: "dashboard.bg.input",
  borderWidth: "2px",
  borderColor: "dashboard.border.default",
  fontWeight: "600",
  transition: "all 160ms ease",
  color: "dashboard.fg.default",
  _placeholder: { color: "dashboard.fg.subtle" },
  _hover: { borderColor: "dashboard.accent.border", bg: "dashboard.bg.surfaceMuted" },
  _focusVisible: { borderColor: "dashboard.accent.border", boxShadow: "0 0 0 3px rgba(229,57,53,0.15)", bg: "dashboard.bg.input" },
};

export function DashboardInput(props) {
  return <Input {...inputBase} {...props} />;
}

export function DashboardTextarea(props) {
  return (
    <Textarea
      rounded="2xl"
      bg="dashboard.bg.input"
      borderWidth="2px"
      borderColor="dashboard.border.default"
      fontWeight="500"
      lineHeight="1.7"
      transition="all 160ms ease"
      color="dashboard.fg.default"
      _placeholder={{ color: "dashboard.fg.subtle" }}
      _hover={{ borderColor: "dashboard.accent.border", bg: "dashboard.bg.surfaceMuted" }}
      _focusVisible={{ borderColor: "dashboard.accent.border", boxShadow: "0 0 0 3px rgba(229,57,53,0.15)", bg: "dashboard.bg.input" }}
      {...props}
    />
  );
}

export function DashboardSelectTrigger({ children, ...props }) {
  return (
    <Select.Trigger
      h="11"
      px="4"
      rounded="pill"
      bg="dashboard.bg.input"
      borderWidth="2px"
      borderColor="dashboard.border.default"
      fontWeight="600"
      transition="all 160ms ease"
      color="dashboard.fg.default"
      _placeholder={{ color: "dashboard.fg.subtle" }}
      _hover={{ borderColor: "dashboard.accent.border", bg: "dashboard.bg.surfaceMuted" }}
      _focusVisible={{ borderColor: "dashboard.accent.border", boxShadow: "0 0 0 3px rgba(229,57,53,0.15)" }}
      {...props}
    >
      {children}
    </Select.Trigger>
  );
}

export function DashboardSelectContent({ children, ...props }) {
  return (
    <Select.Content
      rounded="xl"
      borderWidth="1px"
      borderColor="dashboard.border.muted"
      boxShadow="0 20px 38px rgba(183,28,28,0.10)"
      p="1.5"
      bg="dashboard.bg.popover"
      {...props}
    >
      {children}
    </Select.Content>
  );
}

export function DashboardSelectItem({ children, ...props }) {
  return (
    <Select.Item
      rounded="lg"
      px="3"
      py="2"
      fontWeight="600"
      color="dashboard.fg.default"
      transition="all 130ms ease"
      cursor="pointer"
      _highlighted={{ bg: "dashboard.accent.subtle", color: "dashboard.accent.fg" }}
      _selected={{ bg: "dashboard.accent.subtle", color: "dashboard.accent.fg" }}
      {...props}
    >
      {children}
    </Select.Item>
  );
}

export function DashboardCheckboxCardControl({
  children,
  accentColor = "dashboard.accent.border",
  selectedVariant = "filled",
  selectedBg,
  ...props
}) {
  const isOutlined = selectedVariant === "outlined";
  const checkedBg = selectedBg ?? (isOutlined ? "dashboard.accent.subtle" : accentColor);

  return (
    <CheckboxCard.Context>
      {(ctx) => {
        const checked = ctx.checked === true;
        return (
          <Box
            p="4"
            rounded="2xl"
            borderWidth={isOutlined ? "3px" : "2px"}
            borderColor={checked && isOutlined ? accentColor : "transparent"}
            bg={checked ? checkedBg : "dashboard.bg.input"}
            color={checked && !isOutlined ? "dashboard.fg.onAccent" : "dashboard.fg.default"}
            transition="all 160ms ease"
            outline="none"
            boxShadow="none"
            cursor="pointer"
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            textAlign="center"
            _hover={{
              borderColor: checked && isOutlined ? accentColor : "transparent",
              bg: checked ? checkedBg : "dashboard.bg.surfaceMuted",
              transform: "translateY(-1px)",
            }}
            _focus={{ outline: "none", boxShadow: "none" }}
            _focusVisible={{ outline: "none", boxShadow: "none" }}
            css={!isOutlined && checked ? { "& svg": { color: "var(--chakra-colors-white)" } } : undefined}
            {...props}
          >
            {children}
          </Box>
        );
      }}
    </CheckboxCard.Context>
  );
}

export function DashboardDeleteButton(props) {
  return (
    <IconButton
      size="sm"
      rounded="pill"
      variant="subtle"
      color="dashboard.feedback.error"
      bg="dashboard.accent.subtle"
      borderWidth="1px"
      borderColor="dashboard.border.muted"
      _hover={{
        bg: "dashboard.accent.solid",
        color: "dashboard.fg.onAccent",
        borderColor: "dashboard.accent.solid",
        transform: "translateY(-1px)",
      }}
      _active={{ transform: "translateY(0)" }}
      {...props}
    >
      {props.children ?? <FiTrash2 />}
    </IconButton>
  );
}

export function DashboardTruncatedText({ children, tooltip, tooltipMaxW = "320px", ...props }) {
  const content = tooltip ?? children;

  if (content == null || content === "") {
    return (
      <Text truncate minW="0" {...props}>
        {children}
      </Text>
    );
  }

  return (
    <Tooltip.Root openDelay={0}>
      <Tooltip.Trigger asChild>
        <Text truncate minW="0" cursor="default" {...props}>
          {children}
        </Text>
      </Tooltip.Trigger>
      <Tooltip.Positioner>
        <Tooltip.Content maxW={tooltipMaxW} whiteSpace="normal">
          {content}
        </Tooltip.Content>
      </Tooltip.Positioner>
    </Tooltip.Root>
  );
}
